/**
 * AmenityNoteField — per-amenity optional note input (SCRUM-141 / DS-010-5).
 *
 * Rendered inline BELOW a selected chip. A labeled `<textarea>` (accessible
 * name mentions the amenity, so a screen-reader user knows which note they are
 * editing), a live `N/500` codepoint counter, and an over-limit invalid state
 * (`aria-invalid` + a visible `role="alert"`).
 *
 * The field is CONTROLLED and does NOT normalize — `onChange` reports the raw
 * typed value. Normalization (trim / whitespace-only → null) mirrors the BE
 * `AmenityNote` VO and is applied at SAVE time by the editor (`normalizeNote`),
 * so the field stays a dumb display of exactly what the user typed. The counter
 * counts Unicode codepoints (`noteCodepointLength`, not UTF-16 units) to match
 * the shared `setPropertyAmenitiesItemSchema` cap semantics.
 *
 * The cap itself is NOT re-implemented here — `AMENITY_NOTE_MAX_LENGTH` is the
 * shared validators constant (CLAUDE.md FE §4). The over-limit UI is advisory;
 * the shared schema is the source of truth (and gates the editor's Save).
 */

import * as React from 'react';
import { AMENITY_NOTE_MAX_LENGTH } from '@artemis/validators/policies';
import { cn } from '@sparta/ui-kit';
import { noteCodepointLength } from '../../api/amenity-note.js';

export interface AmenityNoteFieldProps {
  /** Amenity label — woven into the textarea's accessible name. */
  readonly amenityLabel: string;
  /** Controlled raw value. */
  readonly value: string;
  /** Reports the raw typed value (no normalization at the field level). */
  readonly onChange: (next: string) => void;
  /** Codepoint cap for the counter + over-limit state. */
  readonly maxLength?: number;
}

export function AmenityNoteField({
  amenityLabel,
  value,
  onChange,
  maxLength = AMENITY_NOTE_MAX_LENGTH,
}: AmenityNoteFieldProps): React.ReactElement {
  const errorId = React.useId();
  const count = noteCodepointLength(value);
  const overLimit = count > maxLength;

  return (
    <div className="flex flex-col gap-1">
      <textarea
        aria-label={`Note for ${amenityLabel}`}
        aria-invalid={overLimit ? true : undefined}
        aria-describedby={overLimit ? errorId : undefined}
        value={value}
        rows={2}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'block w-full rounded-lg border border-solid bg-[var(--bg-surface)] px-3 py-2',
          'text-sm text-[color:var(--fg-primary)] outline-none',
          'focus-visible:border-[var(--border-focus)]',
          'placeholder:text-[color:var(--fg-tertiary)]',
          overLimit
            ? 'border-[var(--fg-danger)]'
            : 'border-[var(--border-default)]',
        )}
      />
      <div className="flex items-center justify-between gap-2">
        {overLimit ? (
          <p
            id={errorId}
            role="alert"
            className="m-0 text-xs text-[color:var(--fg-danger)]"
          >
            Note is too long — must be at most {maxLength} characters.
          </p>
        ) : (
          <span aria-hidden="true" />
        )}
        <span
          className={cn(
            'text-xs tabular-nums',
            overLimit
              ? 'text-[color:var(--fg-danger)]'
              : 'text-[color:var(--fg-tertiary)]',
          )}
        >
          {count}/{maxLength}
        </span>
      </div>
    </div>
  );
}
